import { useMemo, useState } from "react";
import { LAUNCH_DATE } from "@griddle/generator";
import { useLocalProgress } from "../hooks/useLocalProgress";
import { ARCHIVE_MONTH_KEY, GAME_STATE_KEY, puzzleKey, getUtcToday } from "../lib/constants";
import styles from "./Archive.module.css";

interface ArchiveProps {
  onSelectPuzzle: (date: string, size: "7x7" | "9x9") => void;
  size: "7x7" | "9x9";
}

// Derived from LAUNCH_DATE for calendar navigation bounds
const LAUNCH_YEAR = parseInt(LAUNCH_DATE.slice(0, 4), 10);
const LAUNCH_MONTH = parseInt(LAUNCH_DATE.slice(5, 7), 10) - 1; // 0-indexed

function loadSavedMonth(): { year: number; month: number } | null {
  try {
    const raw = localStorage.getItem(ARCHIVE_MONTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveMonth(year: number, month: number): void {
  try {
    localStorage.setItem(ARCHIVE_MONTH_KEY, JSON.stringify({ year, month }));
  } catch { /* ignore */ }
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function loadGameStates(): Record<string, { swapsUsed: number; maxSwaps?: number }> {
  try {
    return JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}");
  } catch {
    return {};
  }
}

export function Archive({ onSelectPuzzle, size }: ArchiveProps) {
  const { progress } = useLocalProgress();
  // Memoized with empty deps — Archive remounts on navigation so this is always fresh
  const gameStates = useMemo(() => loadGameStates(), []);
  const todayStr = useMemo(() => getUtcToday(), []); // UTC YYYY-MM-DD
  const todayParts = useMemo(() => {
    const [y, m] = todayStr.split("-").map(Number);
    return { year: y, month: m - 1 }; // month is 0-indexed
  }, [todayStr]);
  const saved = useMemo(() => loadSavedMonth(), []);

  const [viewYear, setViewYear] = useState(saved?.year ?? todayParts.year);
  const [viewMonth, setViewMonth] = useState(saved?.month ?? todayParts.month);

  const monthName = new Date(viewYear, viewMonth).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDayOfWeek = new Date(Date.UTC(viewYear, viewMonth, 1)).getUTCDay();

  const canGoPrev =
    viewYear > LAUNCH_YEAR ||
    (viewYear === LAUNCH_YEAR && viewMonth > LAUNCH_MONTH);

  const canGoNext =
    viewYear < todayParts.year ||
    (viewYear === todayParts.year && viewMonth < todayParts.month);

  const prevMonth = () => {
    if (!canGoPrev) return;
    let newMonth = viewMonth - 1;
    let newYear = viewYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    setViewMonth(newMonth);
    setViewYear(newYear);
    saveMonth(newYear, newMonth);
  };

  const nextMonth = () => {
    if (!canGoNext) return;
    let newMonth = viewMonth + 1;
    let newYear = viewYear;
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setViewMonth(newMonth);
    setViewYear(newYear);
    saveMonth(newYear, newMonth);
  };

  return (
    <div className={styles.archive}>
      <h2 className={styles.title}>Archive</h2>

      <div className={styles.monthNav}>
        <button
          className={styles.navButton}
          onClick={prevMonth}
          disabled={!canGoPrev}
        >
          &lt;
        </button>
        <span className={styles.monthLabel}>{monthName}</span>
        <button
          className={styles.navButton}
          onClick={nextMonth}
          disabled={!canGoNext}
        >
          &gt;
        </button>
      </div>

      <div className={styles.weekdays}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className={styles.weekday}>
            {d}
          </div>
        ))}
      </div>

      <div className={styles.calendar}>
        {Array.from({ length: firstDayOfWeek }, (_, i) => (
          <div key={`empty-${i}`} className={styles.emptyDay} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const dateStr = formatDate(viewYear, viewMonth, day);
          const isFuture = dateStr > todayStr;
          const isBeforeLaunch = dateStr < LAUNCH_DATE;
          const isAvailable = !isFuture && !isBeforeLaunch;
          const progressKey = puzzleKey(size, dateStr);
          const entry = progress[progressKey];
          const isSolvedFirstTry = entry?.solved && !entry?.overLimit && entry?.firstTry;
          const isSolvedRetry = entry?.solved && !entry?.overLimit && !entry?.firstTry;
          const gameState = gameStates[progressKey];
          const isUnsolvedOverLimit = !entry?.solved && gameState?.maxSwaps != null && gameState.swapsUsed >= gameState.maxSwaps;
          const isFailed = (entry?.solved && entry?.overLimit) || isUnsolvedOverLimit;
          const isFailedUnsolved = isUnsolvedOverLimit;
          const isInProgress = !entry?.solved && !isUnsolvedOverLimit && gameState?.swapsUsed > 0;
          const isToday = dateStr === todayStr;

          return (
            <button
              key={day}
              className={`${styles.day} ${!isAvailable ? styles.unavailable : ""} ${isAvailable && isSolvedFirstTry ? styles.solved : ""} ${isAvailable && isSolvedRetry ? styles.solvedRetry : ""} ${isAvailable && isFailed && !isFailedUnsolved ? styles.failed : ""} ${isAvailable && isFailedUnsolved ? styles.failedUnsolved : ""} ${isAvailable && isInProgress ? styles.inProgress : ""} ${isAvailable && isToday ? styles.today : ""}`}
              disabled={!isAvailable}
              onClick={() => onSelectPuzzle(dateStr, size)}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendSolved}`} /> Solved
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendSolvedRetry}`} /> Retry
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendInProgress}`} /> In progress
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendFailed}`} /> Over limit
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendToday}`} /> Today
        </span>
      </div>
    </div>
  );
}
